import { useEffect, useRef } from "react";
import {
  bigDealAmount,
  catchUpDarkMs,
  catchUpOfflineMs,
  mediumDealAmount,
  personalBestMinAmount,
  recordMinAmount,
  saleConfettiMax,
  saleConfettiMin,
} from "../constants";
import { chargeFullMs, chargeMs } from "../animations/charge";
import { currentGlowMs, isPlainAlert, voiceAt } from "../animations/horn";
import { bestHoldMs, recordBuildMs, stampHitMs } from "../animations/recordBuild";
import { targetPartyGroup } from "../animations/party";
import { feedLandMs, feedSettleMs } from "../animations/targetFeed";
import { byId, durationOf } from "../animations/registry";
import { readBests, readRecord, writeBests, writeRecord } from "../utils/saleRecord";
import { readLastSeen, touchLastSeen, writeLastSeen } from "../utils/lastSeen";
import { isScreenAwake, onScreenChange } from "../utils/screenAwake";

// Vertaalt wat er tussen twee ophaalrondes is bijgekomen naar animaties.
// De eerste meting is de nulstand, behalve na een herstart: dan geldt de
// bewaarde stand van vlak voor de tv uitging (utils/lastSeen.js).

function snapshot(data) {
  const totals = new Map();
  const order = [];

  (data.rankList || []).forEach((user) => {
    if (!user?.name) return;
    totals.set(user.name, user.totalPrice || 0);
    order.push(user.name);
  });

  // Uit de diensten halen we wat er verkocht is.
  const services = new Map();
  (data.soldServices || []).forEach((service) => {
    if (!service?.name) return;
    services.set(service.name, service.totalPrice || 0);
  });

  const target = data.teamTarget?.amount || 0;
  const percent = target
    ? Math.min(100, Math.round(((data.scoredTowardsTarget || 0) / target) * 100))
    : 0;

  return { totals, order, services, amount: data.amount || 0, percent };
}

// Van en naar localStorage: Maps worden gewone objecten.
function pack(snap, scope) {
  return {
    scope,
    at: snap.at,
    totals: Object.fromEntries(snap.totals),
    order: snap.order,
    services: Object.fromEntries(snap.services),
    amount: snap.amount,
    percent: snap.percent,
  };
}

function unpack(record) {
  return {
    totals: new Map(Object.entries(record.totals || {})),
    order: record.order || [],
    services: new Map(Object.entries(record.services || {})),
    amount: record.amount || 0,
    percent: record.percent || 0,
    at: record.at,
  };
}

// Per naam hoeveel er is bijgekomen, in de volgorde van de Map.
function increases(before, now) {
  const grown = [];
  now.forEach((price, name) => {
    const was = before.get(name) ?? 0;
    if (price > was) grown.push({ name, amount: price - was });
  });
  return grown;
}

const largestFirst = (a, b) => b.amount - a.amount;

// Pakt de dienst met precies dit bedrag en haalt hem uit de lijst, zodat
// twee gelijke verkopen niet dezelfde dienst krijgen. Geen match: null.
function takeService(grown, amount) {
  const index = grown.findIndex((service) => service.amount === amount);
  if (index < 0) return null;
  return grown.splice(index, 1)[0].name;
}

// Nieuwe verkopen, grootste eerst, met de bijbehorende dienst. Eén verkoop
// en één gegroeide dienst horen altijd bij elkaar, ook bij ongelijk bedrag.
function salesBetween(before, now) {
  const sales = increases(before.totals, now.totals).sort(largestFirst);
  const grown = increases(before.services, now.services).sort(largestFirst);
  sales.forEach((sale) => {
    sale.service = takeService(grown, sale.amount);
  });
  if (sales.length === 1 && !sales[0].service && grown.length === 1) {
    sales[0].service = grown[0].name;
  }

  return sales;
}

// De grootste stijger in de ranglijst, of null.
function biggestClimber(before, now) {
  let climber = null;
  now.order.forEach((name, index) => {
    const was = before.order.indexOf(name);
    if (was < 0 || was <= index) return;
    const jump = was - index;
    if (!climber || jump > climber.jump) {
      climber = { name, position: index + 1, from: was + 1, jump };
    }
  });
  return climber;
}

// Tijdlijn van één verkoop (ms vanaf de start van het groepje): schermrand
// als seintje, aanloop op donker beeld, aankondiging met confetti, dan
// lichten rij en meter op en daarna pas het kaartje van de stijger.
//
// De aankondiging start 0,4s voor het einde van de aanloop: zo lang duurt
// fx-splash-dim om op te komen, dus er valt geen licht gat tussen.
const chargeHandoff = chargeMs - 400;

const cardConfettiAfterSplash = 900; // de naam staat er dan
const cannonsAfterSplash = [1200, 4200, 6800];
const flashAfterCard = 200;
const climbAfterFlash = byId("row-flash").ms;

// Bij een record slaat de stempel erop zodra de aankondiging leesbaar is
// (na 1,3s plus een tel), 1,6s voor het record zelf.
const stampAfterSplash = 2400;
const recordAfterSplash = stampAfterSplash + 1600;

// Bordrecord: de recordkaart neemt het donker over terwijl de splash wegfadet.
const recordSplashMs = recordAfterSplash + 600;

// Eigen record: de aankondiging draait om en blijft staan.
const bestSplashMs = recordAfterSplash + bestHoldMs;
// Het geluid zwelt aan tot de stempel neerkomt.
const buildAfterSplash = stampAfterSplash + stampHitMs - recordBuildMs;

// Adempauze tussen een verkoop en het targetfeest; confetti en kanonnen
// tellen niet mee voor wanneer het bord rustig is.
const calmBeforeTarget = 1500;

// Wanneer het bord rustig is: alles uitgespeeld behalve confetti en kanonnen.
function settledAt(events) {
  return events.reduce((latest, [id, payload, at]) => {
    if (id === "confetti-burst" || id === "party-cannons") return latest;
    return Math.max(latest, at + durationOf(byId(id), payload));
  }, 0);
}

function targetAfter(events) {
  return settledAt(events) + calmBeforeTarget;
}

const shifted = (events, by) => events.map(([id, payload, start]) => [id, payload, start + by]);

// Bij Film geen adempauze: het bedrag is al de meter in gevlogen (target-feed in
// saleGroup), en als de meter veert vertrekt hij meteen naar 100. De stijger wacht tot het
// feest voorbij is, anders valt zijn kaartje midden in de aanloop.
const climbIds = ["rank-up", "podium-bounce"];

function feedIntoTarget(events, targetGroup) {
  const [, , landAt] = events.find(([id]) => id === "gauge-pulse");
  const kept = events.filter(([id]) => !climbIds.includes(id));
  const target = shifted(targetGroup, landAt + feedSettleMs);
  const climbAt = settledAt(target) + calmBeforeTarget;
  const climb = events
    .filter(([id]) => climbIds.includes(id))
    .map(([id, payload]) => [id, payload, climbAt]);

  return [...kept, ...target, ...climb];
}

// Confetti loopt lineair mee met het bedrag, tot saleConfettiMax bij bigDealAmount.
function confettiFor(amount) {
  const part = Math.min(1, Math.max(0, amount / bigDealAmount));
  return saleConfettiMin + (saleConfettiMax - saleConfettiMin) * part;
}

// Eén verkoop als groepje [animatie, gegevens, tijd]. record of best (hooguit
// één) is de verbroken lat: dan volgen stempel en vuurwerk of eigen record.
// climber: de stijger als dat deze verkoper is.
function saleGroup(sale, { record = null, best = null, climber = null } = {}) {
  const beaten = record || best;
  const film = !isPlainAlert();
  // Even lang als screen-glow, zodat de aanloop start als de rand weg is.
  const glowLead = currentGlowMs();
  const splashAt = glowLead + chargeHandoff;
  // Wanneer het bord weer in beeld staat.
  let boardAt = splashAt + byId("sale-splash").ms;

  let splash = sale;
  if (record) {
    splash = { ...sale, ms: recordSplashMs, handoff: true };
  } else if (best) {
    splash = {
      ...sale,
      ms: bestSplashMs,
      best: { previous: best.amount, at: recordAfterSplash },
    };
  }

  const events = [["screen-glow", {}, 0]];
  if (film) events.push(["sale-attention", {}, voiceAt]);
  // Het bedrag gaat mee: een grote deal krijgt een zwaardere aanloop (bij Film).
  events.push(["sale-charge", { amount: sale.amount }, glowLead]);
  // Bij Film spat de rij uit elkaar als hij vol is, in een eigen laag boven de aankondiging.
  if (film) {
    events.push(["charge-burst", { big: sale.amount >= bigDealAmount }, glowLead + chargeFullMs]);
  }
  events.push(["sale-splash", splash, splashAt]);

  if (beaten) {
    const recordId = record ? "fireworks" : "personal-best";
    const recordAt = splashAt + recordAfterSplash;
    events.push(
      ["record-build", {}, splashAt + buildAfterSplash],
      ["record-stamp", { kind: record ? "record" : "best" }, splashAt + stampAfterSplash],
      [
        recordId,
        {
          name: sale.name,
          amount: sale.amount,
          service: sale.service,
          previous: beaten.amount,
          // Na de stempel: bij Film zit het geluid al in de stem op de stempel.
          afterStamp: true,
          // Eigen record: alleen nog het geluid, het beeld zit in de splash.
          ...(record ? {} : { continued: true }),
        },
        recordAt,
      ],
    );
    boardAt = record ? recordAt + byId(recordId).ms : splashAt + bestSplashMs;
  }

  // Bij Film vliegt het bedrag de meter in zodra het bord terug is, en veert de meter als
  // het landt; pas dan lopen meter en balk op (useHeldTeam). Bij Oud veert hij met de rij.
  const flashAt = boardAt + flashAfterCard;
  const pulseAt = film ? boardAt + feedLandMs : flashAt;
  if (film) events.push(["target-feed", { amount: sale.amount }, boardAt]);
  events.push(
    ["gauge-pulse", {}, pulseAt],
    ["row-flash", { name: sale.name }, flashAt],
  );

  // Confetti bij elke verkoop, behalve bij een bordrecord (dan vuurwerk).
  if (!record) {
    events.push([
      "confetti-burst",
      { count: confettiFor(sale.amount) },
      splashAt + cardConfettiAfterSplash,
    ]);

    // Eén tot drie salvo's met geluid, naar gelang de grootte van de verkoop.
    const salvos = sale.amount >= bigDealAmount ? 3 : sale.amount >= mediumDealAmount ? 2 : 1;
    cannonsAfterSplash
      .slice(0, salvos)
      .forEach((at) => events.push(["party-cannons", { cue: "sale-confetti" }, splashAt + at]));
  }

  if (climber) {
    const climbAt = flashAt + climbAfterFlash;
    events.push(["rank-up", climber, climbAt]);
    if (climber.position <= 3) events.push(["podium-bounce", {}, climbAt]);
  }

  return events;
}

// Groepjes die de animatiemotor na elkaar afspeelt, één per verkoop.
// record: de grootste verkoop tot nu toe; onbekend betekent geen vuurwerk.
// catchUp: inhalen na een herstart. De verkopen staan dan al op "Verkopen
// gemist", dus alleen de stijger en het target volgen, zonder records.
function toGroups(before, now, { record = null, bests = {}, catchUp = false } = {}) {
  const targetHit = now.percent >= 100 && before.percent < 100;
  const sales = catchUp ? [] : salesBetween(before, now);
  const climber = biggestClimber(before, now);
  const groups = [];

  sales.forEach((sale, index) => {
    // Alleen de grootste van de ronde kan het bordrecord breken.
    const isRecord =
      index === 0 &&
      record &&
      sale.amount > record.amount &&
      sale.amount >= recordMinAmount;

    // Geen bekende eigen lat: dan is het geen eigen record.
    const best = bests[sale.name] || null;
    const isBest =
      !isRecord &&
      best &&
      sale.amount > best.amount &&
      sale.amount >= personalBestMinAmount;

    groups.push(
      saleGroup(sale, {
        record: isRecord ? record : null,
        best: isBest ? best : null,
        climber: climber?.name === sale.name ? climber : null,
      }),
    );
  });

  // Een stijger zonder eigen verkoop krijgt een los kaartje.
  if (climber && !sales.some((sale) => sale.name === climber.name)) {
    const group = [["rank-up", climber]];
    if (climber.position <= 3) group.push(["podium-bounce", {}]);
    groups.push(group);
  }

  // Het targetfeest hangt achter de laatste verkoop in hetzelfde groepje,
  // anders volgt een lange wachtpauze. Zonder verkoop: een eigen groepje.
  if (targetHit) {
    const targetGroup = targetPartyGroup(now.amount);
    const last = groups[groups.length - 1];
    const afterSale = last?.some(([id]) => id === "sale-splash");
    if (afterSale && !isPlainAlert()) {
      groups[groups.length - 1] = feedIntoTarget(last, targetGroup);
    } else if (afterSale) {
      groups[groups.length - 1] = [...last, ...shifted(targetGroup, targetAfter(last))];
    } else {
      groups.push(targetGroup);
    }
  }

  // Alleen geld erbij, geen verkoop die opvalt: dan alleen de meter.
  if (!catchUp && groups.length === 0 && now.amount > before.amount) {
    groups.push([["gauge-pulse", {}]]);
  }

  return groups;
}

// Dezelfde vergelijking los, voor het animatielab.
export function diffGroups(
  beforeData,
  nowData,
  record = readRecord(),
  bests = readBests(),
) {
  return toGroups(snapshot(beforeData), snapshot(nowData), { record, bests });
}

// scope is de maand: wisselt die, dan begint het vergelijken opnieuw.
export function useSaleEvents(data, playGroup, scope) {
  const previous = useRef(null);
  const scopeRef = useRef(scope);
  // Record en eigen records lopen door over maanden heen.
  const record = useRef(readRecord());
  const bests = useRef(readBests());
  // Gemeten donkere tijd sinds de vorige ronde; daar gaat het inhalen op af.
  const darkFor = useRef(0);

  // Gaat het scherm uit, dan krijgt de marker het moment van uitgaan. Bij
  // aangaan tellen we de donkere tijd op, want de tv kan vaker kort uitgaan.
  useEffect(
    () =>
      onScreenChange(({ awake, darkAt, sleptMs }) => {
        if (awake) {
          darkFor.current += sleptMs || 0;
          return;
        }
        if (!darkAt) return;
        if (previous.current) previous.current.at = darkAt;
        touchLastSeen(scopeRef.current, darkAt);
      }),
    [],
  );

  useEffect(() => {
    if (scopeRef.current !== scope) {
      scopeRef.current = scope;
      previous.current = null;
    }

    if (!data) {
      previous.current = null;
      return undefined;
    }

    // Scherm donker: ronde overslaan zodat de marker blijft staan voor het inhalen.
    if (!isScreenAwake()) return undefined;

    const darkMs = darkFor.current;
    darkFor.current = 0;

    const now = { ...snapshot(data), at: Date.now() };

    // Na een herstart geldt de bewaarde stand als vorige meting.
    const stored = previous.current ? null : readLastSeen(scope);
    const before = previous.current || (stored ? unpack(stored) : null);
    previous.current = now;
    writeLastSeen(pack(now, scope));
    if (!before) return undefined;

    // Inhalen: "Verkopen gemist" en daarna stijger en target, met keep. Donker
    // scherm is gemeten (korte drempel); stilstand van de browser zien we
    // alleen aan het gat in de bewaarde stand (ruimere drempel).
    if (darkMs >= catchUpDarkMs || now.at - before.at >= catchUpOfflineMs) {
      const sales = salesBetween(before, now);
      const amount = sales.reduce((sum, sale) => sum + sale.amount, 0);
      // Ook zonder verkopen: de kaart laat zien dat de tv weer leeft.
      playGroup([["missed-sales", { since: before.at, amount, sales }]], {
        keep: true,
      });
      toGroups(before, now, { catchUp: true }).forEach((group) =>
        playGroup(group, { keep: true }),
      );
      return undefined;
    }

    const groups = toGroups(before, now, {
      record: record.current,
      bests: bests.current,
    });

    // Lat bijwerken na toGroups, want die vergelijkt nog met de oude. Ook
    // verkopen onder de minimumbedragen tellen mee.
    const grown = increases(before.totals, now.totals);
    const [biggest] = [...grown].sort(largestFirst);
    if (biggest && biggest.amount > (record.current?.amount ?? 0)) {
      record.current = { ...biggest, at: new Date().toISOString() };
      writeRecord(record.current);
    }

    const raised = { ...bests.current };
    let changed = false;
    grown.forEach(({ name, amount }) => {
      if (amount <= (raised[name]?.amount ?? 0)) return;
      raised[name] = { amount, at: new Date().toISOString() };
      changed = true;
    });
    if (changed) {
      bests.current = raised;
      writeBests(raised);
    }

    groups.forEach((group) => playGroup(group));

    return undefined;
  }, [data, playGroup, scope]);
}
